import React, { useEffect, useState } from "react";
import { Timer, ArrowRight } from "lucide-react";

const useCountdown = () => {
  const [remaining, setRemaining] = useState({ h: 71, m: 23, s: 47 });

  useEffect(() => {
    const tick = () =>
      setRemaining((p) => {
        let { h, m, s } = p;
        s -= 1;
        if (s < 0) {
          s = 59;
          m -= 1;
        }
        if (m < 0) {
          m = 59;
          h -= 1;
        }
        if (h < 0) {
          h = 71;
          m = 59;
          s = 59;
        }
        return { h, m, s };
      });
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return remaining;
};

const Cell = ({ value, label }) => (
  <div className="flex-1 bg-black/40 border border-white/10 rounded-xl p-4 md:p-6 text-center backdrop-blur-sm">
    <div className="font-heading text-5xl md:text-7xl text-white tabular-nums leading-none">
      {String(value).padStart(2, "0")}
    </div>
    <div className="text-[10px] md:text-xs uppercase tracking-[0.25em] text-neutral-400 font-semibold mt-3">
      {label}
    </div>
  </div>
);

const MatchWindow = () => {
  const { h, m, s } = useCountdown();

  return (
    <section
      id="match-window"
      data-testid="match-window-section"
      className="relative py-24 md:py-32 bg-[#0A0A0A]"
    >
      <div className="container mx-auto max-w-7xl px-6 md:px-10">
        <div className="relative bg-gradient-to-br from-[#111111] to-[#0A0A0A] border-l-4 border-[#C6FF00] rounded-2xl p-8 md:p-16 lg:p-24 overflow-hidden grid-bg">
          {/* Glow */}
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-[#C6FF00]/10 blur-[140px] rounded-full pointer-events-none" />

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-[#C6FF00]/10 border border-[#C6FF00]/30 mb-6">
                <Timer size={14} className="text-[#C6FF00]" />
                <span className="text-xs uppercase tracking-[0.25em] text-[#C6FF00] font-semibold">
                  72-hour Match Window
                </span>
              </div>

              <h2
                data-testid="match-window-headline"
                className="font-heading text-5xl md:text-7xl lg:text-[7rem] text-white leading-[0.9]"
              >
                MATCH.
                <br />
                SHOW UP.
                <br />
                <span className="text-[#C6FF00]">OR MISS IT.</span>
              </h2>

              <p className="mt-8 text-base md:text-lg text-neutral-300 max-w-xl leading-relaxed">
                Set a workout. Show up. Or move on. Matches expire in 72 hours so chemistry
                turns into chalk dust on the bar — not endless small talk.
              </p>

              <button
                data-testid="match-window-cta-btn"
                onClick={() =>
                  document
                    .querySelector("#final-cta")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="group mt-10 inline-flex items-center gap-2 bg-[#C6FF00] hover:bg-[#AEE600] text-[#0A0A0A] font-bold text-sm md:text-base uppercase tracking-[0.2em] px-8 py-4 rounded-md transition-all duration-300 hover:scale-105 shadow-[0_0_24px_rgba(198,255,0,0.35)] hover:shadow-[0_0_44px_rgba(198,255,0,0.55)]"
              >
                Start Matching
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            <div className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden border border-white/10 mb-6 hidden md:block">
                <img
                  src="https://images.unsplash.com/photo-1572459815549-873917ec8c0a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHxmaXQlMjBibGFjayUyMGNvdXBsZSUyMGd5bXxlbnwwfHx8fDE3NzcxNTg5OTF8MA&ixlib=rb-4.1.0&q=85"
                  alt="Couple celebrating after a session"
                  className="w-full h-48 object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/30 to-transparent" />
                <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-white/80 font-semibold">
                    Show up. Together.
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-[#C6FF00] text-[#0A0A0A] text-[9px] font-bold uppercase tracking-wider">
                    Live
                  </span>
                </div>
              </div>

              <div className="text-xs uppercase tracking-[0.25em] text-neutral-400 mb-4 text-center font-semibold">
                Your match expires in
              </div>
              <div className="flex gap-3" data-testid="match-window-countdown">
                <Cell value={h} label="Hours" />
                <Cell value={m} label="Minutes" />
                <Cell value={s} label="Seconds" />
              </div>
              <div className="mt-6 flex items-center justify-center gap-2 text-xs text-neutral-500">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C6FF00] animate-pulse" />
                Live demo timer
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MatchWindow;
